/**
 * @module
 * @private
 */
var enums = require('./enums');
var TagError = require('./error').Error;

/**
 * Helper function to parse a ViewTags value + valueType and return the coerced value
 * @param {ValueType} valueType
 * @param {*} value
 * @return {string|number} Parsed value
 * @throws {module:tags/error.Error} Throws an error if ViewTag type is unknown or value cannot be parsed
 */
module.exports = function(valueType:ValueType, value:any): string|number {
  var val;
  switch (valueType) {
    case enums.valueType.STRING:
      return value ? String(value) : '';
    case enums.valueType.NUMBER:
      val = parseNumber(value);
      if (isNaN(val)) {
        throw new TagError('"' + value + '" cannot be parsed as a number');
      }
      return val;
    case enums.valueType.CURRENCY:
      // Remove possible currency symbols before&after the number
      val = String(value).replace(/^[^\d\,\.\-]*/g, '').replace(/[^\d\,\.\-]*$/g, '');
      val = parseNumber(val);
      if (isNaN(val)) {
        throw new TagError('"' + value + '" cannot be parsed as currency');
      }
      // Always return amount in cents
      return Math.round(val * 100);
  }

  throw new TagError('Unknown ViewTag type: ' + valueType);
};

function parseNumberWithSeparators(value, groupSeparator, decimalSeparator) {
  /* eslint-disable quotes */
  var re = new RegExp(
    "^\\s*" + // line start plus optional whitespaces
    "([+-]?)" + // md[1] optional sign
    "\\s*" + // optional whitespaces
    "(" + // md[2] pre-decimal part
      "(\\d+)" + // md[3] mandatory numbers - in most simple case this is the number
      "(" + // md[4] - groups
        "(?:" +
          "\\" + groupSeparator +
          "\\d{3}" +
        ")+" + // one or more of 3-digit group followed by separator
      ")?" + // groups can be absent
    ")" + // /md[2]
    "(?:" + // optional decimal part with decimal separator
      "\\" + decimalSeparator +
      "(" + // md[5] decimal part
        "\\d+" + // numbers in decimal part
      ")" + // /md[5]
    ")?" +
    "\\s*$" // optional whitespaces at the end of the string
  );
  /* eslint-enable quotes */

  var md = value.match(re);
  if(!md) {
    return NaN;
  }

  var firstGroup = md[3]; // first group of numbers
  var groups = md[4]; // numbers preceded by group separator (optional)
  var fraction = md[5]; // numbers in decimal part (optional)

  // some corner scenarios - in general we couldn't know how to parse numbers like "123.456" without exactly knowing
  // group/decimal separator.
  // Tradeoff is to consider "1.234" as integer and "1.23" and "1.2345" as floats.
  // Also "123.456" is integer but "1234.567" will be float
  if (
    // if given input is in form of "1234,567" or "12345,567" (no decimal separator, first part has 3+ digits) -
    // groups part is exactly 3 digits (plus separator) - treat it as float
    (groups && !fraction && firstGroup.length > 3 && groups.length === 4) ||

    // if given input is in form of "1.234" or "12.345" or "123.456" (no group separator, first part has 3- digits) -
    // decimal part is exactly 3 digits - treat it as integer
    (!groups && firstGroup.length <= 3 && fraction && fraction.length === 3)
  ) {
    // another set of separators detected
    return NaN;
  }

  var sign = md[1]; // plus-minus sign (optional)
  var integer = md[2].replace(new RegExp('\\' + groupSeparator, 'g'), ''); // digits before decimal separator
  return Number(sign + integer + (fraction ? '.' + fraction : ''));
}

/**
 * Parse given number trying to guess what which group/decimal separators are used in it. Because we don't know the
 * format of input (depending on user settings it may be using different separators to format the number and we cannot
 * know the format)
 * @param value {string|number} Input value
 * @returns {number|NaN} Returns parsed number or undefined value if number cannot be parsed from given string
 */
function parseNumber(value) {
  value = String(value);
  // try comma as group separator
  var res = parseNumberWithSeparators(value, ',', '.');
  if(isNaN(res)) {
    // try dot as group separator
    res = parseNumberWithSeparators(value, '.', ',');
  }
  return res;
}
